import Table from "../../components/RecordTable";
import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  Space,
  message,
  Popconfirm,
  Tag,
  Card,
  Row,
  Col,
  Statistic,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import API from "../../services/api"; // Import main API object

const LabPartners = () => {
  const [form] = Form.useForm();
  const { labId } = useParams();
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [partners, setPartners] = useState([]);
  const [editingPartner, setEditingPartner] = useState(null);
  const [locationInput, setLocationInput] = useState("");
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    fetchPartners();
  }, [labId]);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const response = await API.labPartner.getAll(labId);
      setPartners(response?.data);
    } catch (error) {
      message.error(error.message || "Failed to fetch lab partners");
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocation = () => {
    if (locationInput.trim()) {
      if (!locations.includes(locationInput.trim())) {
        setLocations([...locations, locationInput.trim()]);
        setLocationInput("");
      } else {
        message.warning("Location already added");
      }
    }
  };

  const handleRemoveLocation = (location) => {
    setLocations(locations.filter((loc) => loc !== location));
  };

  const handleOpenModal = (partner = null) => {
    if (partner) {
      setEditingPartner(partner);
      form.setFieldsValue({
        labName: partner.labName,
      });
      setLocations(partner.locations || []);
    } else {
      setEditingPartner(null);
      form.resetFields();
      setLocations([]);
    }
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingPartner(null);
    form.resetFields();
    setLocations([]);
    setLocationInput("");
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const partnerData = {
        ...values,
        locations,
        labAdminId: labId,
      };

      if (editingPartner) {
        await API.labPartner.update(editingPartner._id, partnerData);
        message.success("Lab partner updated successfully");
      } else {
        await API.labPartner.create(partnerData);
        message.success("Lab partner created successfully");
      }

      handleCloseModal();
      fetchPartners();
    } catch (error) {
      message.error(error.message || "Failed to save lab partner");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await API.labPartner.delete(id);
      message.success("Lab partner deleted successfully");
      fetchPartners();
    } catch (error) {
      message.error(error.message || "Failed to delete lab partner");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Lab Name",
      dataIndex: "labName",
      key: "labName",
      render: (text) => <strong style={{ color: "#0A6EBD" }}>{text}</strong>,
    },
    {
      title: "Locations",
      dataIndex: "locations",
      key: "locations",
      render: (locations) => (
        <Space size={[0, 8]} wrap>
          {locations && locations?.length > 0 ? (
            locations.map((location, index) => (
              <Tag key={index} icon={<EnvironmentOutlined />} color="blue">
                {location}
              </Tag>
            ))
          ) : (
            <Tag color="default">No locations</Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Total Locations",
      key: "totalLocations",
      render: (_, record) => (
        <Tag color="cyan">{record.locations?.length || 0}</Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            ghost
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Lab Partner"
            description="Are you sure you want to delete this lab partner?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Total Lab Partners"
              value={partners?.length}
              prefix={<TeamOutlined />}
              styles={{ content: { color: "#0A6EBD" } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Total Locations"
              value={partners?.reduce(
                (acc, partner) => acc + (partner.locations?.length || 0),
                0
              )}
              prefix={<EnvironmentOutlined />}
              styles={{ content: { color: "#0A6EBD" } }}
            />
          </Card>
        </Col>
      </Row>

      {/* Header with Add Button */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h2 style={{ margin: 0, color: "#0A6EBD" }}>Lab Partners</h2>
          <p style={{ margin: 0, color: "#666" }}>
            Manage your lab partner network
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenModal()}
          size="large"
          style={{
            background: "linear-gradient(135deg, #0A6EBD, #0A6EBD)",
            border: "none",
            borderRadius: 8,
          }}
        >
          Add Lab Partner
        </Button>
      </div>

      {/* Partners Table */}
      <Table
        columns={columns}
        dataSource={partners}
        rowKey="_id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} lab partners`,
        }}
        bordered
        style={{
          borderRadius: 12,
          overflow: "hidden",
        }}
        scroll={{ x: "max-content" }}
        components={{
          body: {
            wrapper: (props) => (
              <tbody {...props} style={{ transition: "none" }} />
            ),
          },
        }}
      />

      {/* Add/Edit Modal */}
      <Modal
        title={
          <div style={{ fontSize: 20, fontWeight: 600, color: "#0A6EBD" }}>
            {editingPartner ? "Edit Lab Partner" : "Add Lab Partner"}
          </div>
        }
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            label="Lab Name"
            name="labName"
            rules={[
              { required: true, message: "Please enter lab name" },
              { min: 3, message: "Lab name must be at least 3 characters" },
            ]}
          >
            <Input
              size="large"
              placeholder="Enter lab name"
              prefix={<TeamOutlined style={{ color: "#0A6EBD" }} />}
            />
          </Form.Item>

          <Form.Item label="Locations">
            <Space.Compact style={{ width: "100%" }}>
              <Input
                size="large"
                placeholder="Enter location and click Add"
                value={locationInput}
                onChange={(e) => setLocationInput(e.target.value)}
                onPressEnter={handleAddLocation}
                prefix={<EnvironmentOutlined style={{ color: "#0A6EBD" }} />}
              />
              <Button
                size="large"
                type="primary"
                onClick={handleAddLocation}
                style={{ background: "#0A6EBD", borderColor: "#0A6EBD" }}
              >
                Add
              </Button>
            </Space.Compact>
          </Form.Item>

          {locations?.length > 0 && (
            <div
              style={{
                padding: 16,
                background: "#f0f9ff",
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  marginBottom: 12,
                  color: "#0A6EBD",
                }}
              >
                Added Locations ({locations?.length})
              </div>
              <Space size={[8, 8]} wrap>
                {locations?.map((location, index) => (
                  <Tag
                    key={index}
                    closable
                    onClose={() => handleRemoveLocation(location)}
                    color="blue"
                    style={{ fontSize: 14, padding: "4px 12px" }}
                  >
                    <EnvironmentOutlined /> {location}
                  </Tag>
                ))}
              </Space>
            </div>
          )}

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={handleCloseModal} size="large">
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                style={{
                  background: "linear-gradient(135deg, #0A6EBD, #0A6EBD)",
                  border: "none",
                }}
              >
                {editingPartner ? "Update Partner" : "Create Partner"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LabPartners;
